import { useState, useCallback } from 'react';
import { notifications } from '@mantine/notifications';
import { useUserAccount } from '../../../../hooks/useUserAccount';
import { useUserAttributes } from '../../../../hooks/useUserAttributes';
import { createCustomer } from '../services/CustomersService';
import { updateAccountCustomerId } from '../../../accounts-management/accounts/services/AccountsService';
import { CreateCustomerInput } from '../types';

export const useBillingSetup = () => {
    const { userAccount, refreshUserAccount } = useUserAccount();
    const attributes = useUserAttributes();
    const [isSettingUp, setIsSettingUp] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const setupBilling = useCallback(async () => {
        if (!userAccount?.id || !attributes?.email) {
            setError('Account information not available. Please try again.');
            return;
        }

        setIsSettingUp(true);
        setError(null);

        try {
            const customerInput: CreateCustomerInput = {
                name: `${attributes.givenName || ''} ${attributes.familyName || ''}`.trim() || attributes.email,
                status: 'ACTIVE',
                contactInfo: {
                    email: attributes.email,
                },
            };

            const customer = await createCustomer(customerInput);
            if (!customer?.id) {
                throw new Error('Failed to create billing profile.');
            }

            await updateAccountCustomerId(userAccount.id, customer.id);

            if (refreshUserAccount) {
                await refreshUserAccount();
            }

            setIsComplete(true);
            notifications.show({
                title: 'Billing Profile Created',
                message: 'Your billing profile has been set up successfully.',
                color: 'green',
            });
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : 'Failed to set up billing profile. Please try again.';
            setError(message);
            notifications.show({
                title: 'Setup Failed',
                message,
                color: 'red',
            });
        } finally {
            setIsSettingUp(false);
        }
    }, [userAccount, attributes, refreshUserAccount]);

    return {
        setupBilling,
        isSettingUp,
        isComplete,
        error,
        hasCustomerId: !!userAccount?.customerId,
    };
};
